import { AggregatedQuestion } from 'types'

/**
 * A lesson is authored once per difficulty tier (see api's
 * buildQuestionIndexMap), so the same conceptual question -- "slide 3,
 * check-for-understanding, index 1" -- has a different questionId per tier.
 * A slot is that shared (slide, section, questionIndex) position, holding
 * each tier's own AggregatedQuestion so the grid can show one column that
 * spans every tier.
 */
export type QuestionSlot = {
	slotKey: string
	slide: number
	section: string
	questionIndex: number
	displayLabel?: string
	byTier: Record<string, AggregatedQuestion>
}

export const buildQuestionSlots = (questions: AggregatedQuestion[]): QuestionSlot[] => {
	const bySlot = new Map<string, QuestionSlot>()

	for (const q of questions) {
		const slotKey = `${q.slide}::${q.section || ''}::${q.questionIndex}`
		let slot = bySlot.get(slotKey)
		if (!slot) {
			slot = {
				slotKey,
				slide: q.slide,
				section: q.section || '',
				questionIndex: q.questionIndex,
				displayLabel: q.displayLabel,
				byTier: {},
			}
			bySlot.set(slotKey, slot)
		}
		if (q.difficulty) slot.byTier[q.difficulty] = q
		if (!slot.displayLabel) slot.displayLabel = q.displayLabel
	}

	return Array.from(bySlot.values()).sort((a, b) => {
		if (a.slide !== b.slide) return a.slide - b.slide
		if (a.section !== b.section) return a.section.localeCompare(b.section)
		return a.questionIndex - b.questionIndex
	})
}

export const average = (nums: number[]): number | null => {
	if (!nums.length) return null
	return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length)
}

export type SlotPercent = { slotIndex: number; pct: number | null }

/** The slot with the lowest known percent, ties broken by first occurrence. */
export const hardestSlot = (slotPercents: SlotPercent[]): SlotPercent | null => {
	const known = slotPercents.filter((s): s is SlotPercent & { pct: number } => s.pct !== null)
	if (!known.length) return null
	return known.reduce((worst, s) => (s.pct < worst.pct ? s : worst))
}

/** Point spread between the highest and lowest tier average -- 0/1 tiers have no spread. */
export const spreadPoints = (tierAverages: number[]): number => {
	if (tierAverages.length < 2) return 0
	return Math.max(...tierAverages) - Math.min(...tierAverages)
}

export type WrongAnswerPattern = { text: string; n: number; wrongTotal: number }

/**
 * Among students marked incorrect on a question, the most commonly chosen
 * wrong answer -- a shared misconception vs. scattered guessing. Grouped by
 * the student's raw answer text so it works for any question type, not just
 * multiple-choice.
 */
export const mostCommonWrongAnswer = (
	entries: { answer: string; correct: boolean }[]
): WrongAnswerPattern | null => {
	const wrong = entries.filter(e => !e.correct && e.answer)
	if (!wrong.length) return null

	const counts = new Map<string, number>()
	for (const e of wrong) counts.set(e.answer, (counts.get(e.answer) || 0) + 1)

	let top = wrong[0].answer
	counts.forEach((n, text) => {
		if (n > (counts.get(top) || 0)) top = text
	})

	return { text: top, n: counts.get(top) || 0, wrongTotal: wrong.length }
}
